import { Mail, Paperclip } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';
import { UnifiedCommunication } from '@/services/types/client-communications-types';

import { useClientCommunications } from '../ClientCommunicationsContext';

interface EmailThreadCardProps {
  email: UnifiedCommunication;
}

export const EmailThreadCard: React.FC<EmailThreadCardProps> = ({ email }) => {
  const { selectCommunication, selectedCommunication, isRead } =
    useClientCommunications();

  const isInbound = email.direction === 'inbound';
  const read = isRead(email.id);
  const isSelected = selectedCommunication?.id === email.id;

  const handleSelect = () => {
    selectCommunication(email);
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div
      className={cn(
        'group flex items-center gap-3 p-3 rounded-xl border bg-white',
        'hover:border-zinc-200 hover:bg-zinc-50/50 transition-all cursor-pointer',
        isSelected
          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
          : 'border-zinc-100',
        !read && isInbound && !isSelected && 'border-l-2 border-l-primary',
      )}
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleSelect();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {/* Icon */}
      <div
        className={cn(
          'flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center',
          isInbound ? 'bg-blue-50' : 'bg-primary/10',
        )}
      >
        <Mail
          className={cn(
            'h-4 w-4',
            isInbound ? 'text-blue-600' : 'text-primary',
          )}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-sm font-semibold text-zinc-900 truncate',
              !read && isInbound && 'text-zinc-950',
            )}
          >
            {email.subject || '(No subject)'}
          </span>
          {email.has_attachments && (
            <Paperclip className="h-3.5 w-3.5 text-zinc-400 flex-shrink-0" />
          )}
          {email.thread_count && email.thread_count > 1 && (
            <span className="text-xs font-semibold text-zinc-500 flex-shrink-0">
              ({email.thread_count})
            </span>
          )}
        </div>
        <p className="text-xs font-medium text-zinc-600 truncate mt-0.5">
          {email.preview || email.content?.substring(0, 60)}
        </p>
      </div>

      {/* Time */}
      <span className="text-xs font-semibold text-zinc-500 flex-shrink-0">
        {formatTime(email.timestamp)}
      </span>
    </div>
  );
};

export default EmailThreadCard;
